import { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useLocation } from "react-router";
import type { JSX } from "react";
import { useSpecular } from "../../hooks/useSpecular";
import { useDialog } from "../../hooks/useDialog";

export type Theme = "dark" | "light" | "neu";

const sections = [
  { label: "观看", hash: "#watch" },
  { label: "系列视频", hash: "#series" },
  { label: "全部视频", hash: "#videos" },
  { label: "关于", hash: "#about" },
];

interface Props {
  theme: Theme;
  onThemeChange: (t: Theme) => void;
  onOpenSearch: () => void;
}

const themeOptions: { id: Theme; label: string; icon: JSX.Element }[] = [
  {
    id: "dark",
    label: "黑色模式",
    icon: (
      <svg viewBox="0 0 24 24" className="h-[14px] w-[14px] fill-none stroke-current" strokeWidth="1.9" aria-hidden>
        <path d="M20.4 14.2A8.5 8.5 0 0 1 9.8 3.6a8.5 8.5 0 1 0 10.6 10.6z" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    id: "light",
    label: "白色模式",
    icon: (
      <svg viewBox="0 0 24 24" className="h-[14px] w-[14px] fill-none stroke-current" strokeWidth="1.9" aria-hidden>
        <circle cx="12" cy="12" r="4.2" />
        <path d="M12 2.5v2.2M12 19.3v2.2M2.5 12h2.2M19.3 12h2.2M4.9 4.9l1.6 1.6M17.5 17.5l1.6 1.6M19.1 4.9l-1.6 1.6M6.5 17.5l-1.6 1.6" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    id: "neu",
    label: "新拟态模式",
    icon: (
      <svg viewBox="0 0 24 24" className="h-[14px] w-[14px] fill-none stroke-current" strokeWidth="1.9" aria-hidden>
        <circle cx="12" cy="12" r="8.5" />
        <circle cx="12" cy="12" r="3" className="fill-current" stroke="none" />
      </svg>
    ),
  },
];

/** 根据鼠标在导航条内的水平位置，返回每一项的放大系数（App Store 式磁性放大） */
function useMagnify(itemCount: number) {
  const barRef = useRef<HTMLDivElement>(null);
  const [scales, setScales] = useState<number[]>(() => Array(itemCount).fill(1));

  const onMove = useCallback(
    (e: React.MouseEvent) => {
      const bar = barRef.current;
      if (!bar) return;
      const items = Array.from(bar.querySelectorAll<HTMLElement>("[data-mag]"));
      const mx = e.clientX;
      const next = items.map((el) => {
        const r = el.getBoundingClientRect();
        const c = r.left + r.width / 2;
        const dist = Math.abs(mx - c);
        // 高斯衰减，半径 ~64px，最大放大 1.35
        const s = 1 + 0.35 * Math.exp(-((dist / 64) ** 2));
        return s;
      });
      setScales(next);
    },
    []
  );

  const onLeave = useCallback(() => setScales(Array(itemCount).fill(1)), [itemCount]);

  return { barRef, scales, onMove, onLeave };
}

export default function Nav({ theme, onThemeChange, onOpenSearch }: Props) {
  const { pathname } = useLocation();
  const home = pathname === "/";
  const hash = (value: string) => (home ? value : `/${value}`);

  const magItems = [...sections, { label: "文章", hash: "/articles" }];
  const { barRef, scales, onMove, onLeave } = useMagnify(magItems.length);
  const navRef = useSpecular<HTMLElement>();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [themeMenuOpen, setThemeMenuOpen] = useState(false);
  const themeMenuRef = useRef<HTMLDivElement>(null);
  const mobilePanelRef = useRef<HTMLDivElement>(null);
  const closeMobileMenu = useCallback(() => setMobileOpen(false), []);

  useDialog({ open: mobileOpen, onClose: closeMobileMenu, containerRef: mobilePanelRef });

  // 主题下拉：点击外部 / Esc 关闭
  useEffect(() => {
    if (!themeMenuOpen) return;
    const onDown = (e: PointerEvent) => {
      if (themeMenuRef.current && !themeMenuRef.current.contains(e.target as Node)) {
        setThemeMenuOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setThemeMenuOpen(false);
    };
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [themeMenuOpen]);

  const activeTheme = themeOptions.find((o) => o.id === theme) ?? themeOptions[0];

  return (
    <motion.header
      initial={{ y: -32, opacity: 0, filter: "blur(8px)" }}
      animate={{ y: 0, opacity: 1, filter: "blur(0px)" }}
      transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      className={`fixed inset-x-0 top-5 flex justify-center px-4 ${mobileOpen ? "z-[70]" : "z-50"}`}
    >
      <nav ref={navRef} className="glass glass-refract glass-live flex h-14 items-center gap-1 rounded-full pl-5 pr-2">
        <a href={hash("#top")} className="mr-3 flex items-center gap-2.5">
          <span className="flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-[#6ec1ff] via-[#2997ff] to-[#7d7aff] text-[13px] font-bold text-white shadow-[0_0_18px_rgba(41,151,255,0.5)]">
            L
          </span>
          {/* 品牌文字：极窄屏隐藏，给导航条留出空间 */}
          <span className="hidden text-[15px] font-semibold tracking-tight t-90 min-[380px]:inline">
            LetsLearning
          </span>
        </a>

        {/* 链接区：App Store 式磁性放大 */}
        <div
          ref={barRef}
          onMouseMove={onMove}
          onMouseLeave={onLeave}
          className="hidden items-center md:flex"
        >
          {sections.map((l, i) => (
            <a
              key={l.hash}
              data-mag
              href={hash(l.hash)}
              style={{
                transform: `scale(${scales[i] ?? 1})`,
                transition: "transform 0.18s cubic-bezier(0.34,1.4,0.5,1)",
              }}
              className="t-60 hover-surface-1 hover-t origin-center rounded-full px-4 py-2 text-[13px] font-medium"
            >
              {l.label}
            </a>
          ))}
          <Link
            data-mag
            to="/articles"
            style={{
              transform: `scale(${scales[sections.length] ?? 1})`,
              transition: "transform 0.18s cubic-bezier(0.34,1.4,0.5,1)",
            }}
            className={`origin-center rounded-full px-4 py-2 text-[13px] font-medium hover-surface-1 hover-t ${
              pathname.startsWith("/articles") ? "t-100" : "t-60"
            }`}
          >
            文章
          </Link>
        </div>

        {/* 移动端汉堡按钮 */}
        <button
          type="button"
          onClick={() => setMobileOpen((value) => !value)}
          aria-label={mobileOpen ? "关闭菜单" : "打开菜单"}
          aria-expanded={mobileOpen}
          className="glass-chip ml-3 flex h-10 w-10 items-center justify-center rounded-full transition-transform duration-500 hover:scale-[1.06] active:scale-[0.94] md:hidden"
          style={{ transitionTimingFunction: "cubic-bezier(0.16,1,0.3,1)" }}
        >
          <span className="relative block h-3 w-[17px]">
            <span
              className={`t-80 absolute left-0 top-0 block h-[1.5px] w-full rounded-full bg-current transition-all duration-300 ${
                mobileOpen ? "top-[5.5px] rotate-45" : ""
              }`}
            />
            <span
              className={`t-80 absolute left-0 top-[11px] block h-[1.5px] w-full rounded-full bg-current transition-all duration-300 ${
                mobileOpen ? "top-[5.5px] -rotate-45" : ""
              }`}
            />
          </span>
        </button>

        {/* 搜索 */}
        <button
          type="button"
          onClick={() => {
            closeMobileMenu();
            onOpenSearch();
          }}
          aria-label="搜索"
          className="glass-chip ml-3 flex h-10 w-10 items-center justify-center rounded-full transition-transform duration-500 hover:scale-[1.06] active:scale-[0.94]"
          style={{ transitionTimingFunction: "cubic-bezier(0.16,1,0.3,1)" }}
        >
          <svg viewBox="0 0 24 24" className="t-80 h-[16px] w-[16px] fill-none stroke-current" strokeWidth="2" aria-hidden>
            <circle cx="11" cy="11" r="7" />
            <path d="M20.5 20.5L16 16" strokeLinecap="round" />
          </svg>
        </button>

        {/* 主题切换：桌面三按钮分段控件，窄屏合并为一个下拉按钮 */}
        {/* —— 窄屏：单按钮 + 下拉（< sm 显示，大幅缩短导航条）—— */}
        <div ref={themeMenuRef} className="relative ml-2 sm:hidden">
          <button
            type="button"
            onClick={() => setThemeMenuOpen((value) => !value)}
            aria-label={`主题：${activeTheme.label}，点击切换`}
            aria-expanded={themeMenuOpen}
            aria-haspopup="listbox"
            className="glass-chip flex h-10 w-10 items-center justify-center rounded-full transition-transform duration-500 hover:scale-[1.06] active:scale-[0.94]"
            style={{ transitionTimingFunction: "cubic-bezier(0.16,1,0.3,1)" }}
          >
            <span className="t-85 flex">{activeTheme.icon}</span>
          </button>
          <AnimatePresence>
            {themeMenuOpen && (
              <motion.div
                initial={{ opacity: 0, y: -8, scale: 0.94 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -6, scale: 0.96 }}
                transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                role="listbox"
                aria-label="选择主题"
                className="glass-deep absolute right-0 top-[calc(100%+10px)] z-[80] w-[168px] rounded-[20px] p-1.5"
              >
                {themeOptions.map((opt) => {
                  const active = theme === opt.id;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      role="option"
                      aria-selected={active}
                      onClick={() => {
                        onThemeChange(opt.id);
                        setThemeMenuOpen(false);
                      }}
                      className={`flex w-full items-center gap-3 rounded-[14px] px-3.5 py-2.5 text-left text-[14px] font-medium transition-colors duration-300 hover-surface-1 ${
                        active ? "t-100" : "t-70"
                      }`}
                    >
                      <span className={`flex ${active ? "text-[#2997ff]" : ""}`}>{opt.icon}</span>
                      <span className="flex-1">{opt.label}</span>
                      {active && (
                        <svg viewBox="0 0 24 24" className="h-4 w-4 fill-none stroke-[#2997ff]" strokeWidth="2.4" aria-hidden>
                          <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      )}
                    </button>
                  );
                })}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* —— 桌面：三按钮分段控件（≥ sm 显示）—— */}
        <div className="glass-chip ml-2 hidden items-center rounded-full p-1 sm:flex" role="group" aria-label="主题切换">
          {themeOptions.map((opt) => {
            const active = theme === opt.id;
            return (
              <button
                key={opt.id}
                type="button"
                onClick={() => onThemeChange(opt.id)}
                aria-label={opt.label}
                title={opt.label}
                className={`relative flex h-8 w-8 items-center justify-center rounded-full transition-colors duration-500 ${
                  active ? "t-100" : "t-45 hover-t-85"
                }`}
              >
                {active && (
                  <motion.span
                    layoutId="theme-pill"
                    className="surface-3 absolute inset-0 rounded-full"
                    transition={{ type: "spring", stiffness: 380, damping: 32 }}
                  />
                )}
                <span className="relative z-10 flex">{opt.icon}</span>
              </button>
            );
          })}
        </div>

        <a
          href="https://space.bilibili.com/25488437"
          target="_blank"
          rel="noreferrer"
          className="btn-contrast ml-2 hidden h-10 items-center gap-2 rounded-full px-4 text-[13px] font-semibold transition-transform duration-500 hover:scale-[1.04] active:scale-[0.97] lg:flex"
          style={{ transitionTimingFunction: "cubic-bezier(0.16,1,0.3,1)" }}
        >
          <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current" aria-hidden>
            <path d="M17.813 4.653h.854c1.51.054 2.769.578 3.773 1.574 1.004.995 1.524 2.249 1.56 3.76v7.36c-.036 1.51-.556 2.769-1.56 3.773s-2.262 1.524-3.773 1.56H5.333c-1.51-.036-2.769-.556-3.773-1.56S.036 18.858 0 17.347v-7.36c.036-1.511.556-2.765 1.56-3.76 1.004-.996 2.262-1.52 3.773-1.574h.774l-1.174-1.12a1.234 1.234 0 0 1-.373-.906c0-.356.124-.658.373-.907l.027-.027c.267-.249.573-.373.92-.373.347 0 .653.124.92.373L9.653 4.44c.071.071.134.142.187.213h4.267a.836.836 0 0 1 .16-.213l2.853-2.747c.267-.249.573-.373.92-.373.347 0 .662.151.929.4.267.249.391.551.391.907 0 .355-.124.657-.373.906zM5.333 7.24c-.746.018-1.373.276-1.88.773-.506.498-.769 1.13-.786 1.894v7.52c.017.764.28 1.395.786 1.893.507.498 1.134.756 1.88.773h13.334c.746-.017 1.373-.275 1.88-.773.506-.498.769-1.129.786-1.893v-7.52c-.017-.765-.28-1.396-.786-1.894-.507-.497-1.134-.755-1.88-.773zM8 11.107c.373 0 .684.124.933.373.25.249.383.56.4.933v2.587c0 .373-.124.684-.373.933-.249.25-.56.374-.933.374s-.684-.125-.933-.374-.373-.56-.373-.933v-2.587c0-.373.124-.684.373-.933.249-.249.56-.373.933-.373zm8 0c.373 0 .684.124.933.373.25.249.383.56.4.933v2.587c-.017.373-.141.684-.373.933-.232.25-.551.374-.933.374-.383 0-.692-.125-.933-.374-.242-.249-.364-.56-.373-.933v-2.587c0-.373.124-.684.373-.933.241-.249.55-.373.933-.373z" />
          </svg>
          关注
        </a>
      </nav>

      {/* ===== 移动端全屏菜单 ===== */}
      {createPortal(
        <AnimatePresence>
          {mobileOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="fixed inset-0 z-[65] md:hidden"
              onClick={closeMobileMenu}
            >
              <div
                className="absolute inset-0"
                aria-hidden="true"
                style={{
                  background: "rgb(var(--ink) / 0.65)",
                  backdropFilter: "blur(16px)",
                  WebkitBackdropFilter: "blur(16px)",
                }}
              />
              <motion.div
                ref={mobilePanelRef}
                initial={{ y: -24, opacity: 0, scale: 0.97 }}
                animate={{ y: 0, opacity: 1, scale: 1 }}
                exit={{ y: -18, opacity: 0, scale: 0.98 }}
                transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                className="glass-deep glass-sheen absolute inset-x-4 top-24 rounded-[26px] p-3"
                onClick={(event) => event.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-label="移动导航"
                tabIndex={-1}
              >
                <div className="flex items-center justify-between px-4 py-2">
                  <span className="t-60 text-[12px] font-semibold tracking-[0.16em]">导航</span>
                  <button
                    type="button"
                    onClick={closeMobileMenu}
                    className="glass-chip flex h-9 w-9 items-center justify-center rounded-full t-80"
                    aria-label="关闭导航"
                  >
                    <svg viewBox="0 0 24 24" className="h-4 w-4 fill-none stroke-current" strokeWidth="2" aria-hidden>
                      <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" />
                    </svg>
                  </button>
                </div>
                {sections.map((l, i) => (
                  <motion.a
                    key={l.hash}
                    href={hash(l.hash)}
                    onClick={closeMobileMenu}
                    initial={{ opacity: 0, x: -14 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.05 + i * 0.05, duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                    className="t-80 hover-surface-1 flex items-center justify-between rounded-2xl px-5 py-4 text-[16px] font-medium"
                  >
                    {l.label}
                    <svg viewBox="0 0 24 24" className="t-30 h-4 w-4 fill-none stroke-current" strokeWidth="2" aria-hidden>
                      <path d="M9 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </motion.a>
                ))}
                <motion.div
                  initial={{ opacity: 0, x: -14 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 + sections.length * 0.05, duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                >
                  <Link
                    to="/articles"
                    onClick={closeMobileMenu}
                    className={`flex items-center justify-between rounded-2xl px-5 py-4 text-[16px] font-medium hover-surface-1 ${
                      pathname.startsWith("/articles") ? "t-100" : "t-80"
                    }`}
                  >
                    文章
                    <svg viewBox="0 0 24 24" className="t-30 h-4 w-4 fill-none stroke-current" strokeWidth="2" aria-hidden>
                      <path d="M9 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </Link>
                </motion.div>
                <div className="divider mx-4 my-2 h-px" />
                <a
                  href="https://space.bilibili.com/25488437"
                  target="_blank"
                  rel="noreferrer"
                  className="btn-contrast mx-2 mb-1 mt-1 flex h-12 items-center justify-center gap-2 rounded-full text-[14px] font-semibold"
                >
                  在 B 站关注 LetsLearning
                </a>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </motion.header>
  );
}
